/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */
/* eslint-disable @typescript-eslint/explicit-function-return-type */ //inferred types are _really_ important here
import { Observable, Subscription } from 'rxjs';
import {
	guessXFromDate,
	Plan,
	Ticket,
	TicketFields,
	TicketType,
	Variance,
	PLAN_CONSTANTS,
	actualOnly,
	plannedOnly,
	reverseLiveMap,
} from '@touchplan/common';
import { CalendarWorkdays } from '../models/CalendarWorkdays';
import { standardizeObservable } from '../duck-type-utils/observable-duck';
import { IObservableLike } from '../interfaces/IObservable';

// For the most part, this file should be viewed as a stop gap to be eventually replaced by a Ticket refactor

export interface ITicketExtraCallbacks {
	// eslint-disable-next-line @rushstack/no-new-null
	getXFromDate: (date: string, dayOffset?: number) => number | null;
	checkForLongestPull: (pullColumn: number, plannedStart: string) => boolean;
	getDefaultWorkdaysForLegacyCompletedTickets: (
		startDate: string,
		finishDate: string,
		roleId?: string
		// eslint-disable-next-line @rushstack/no-new-null
	) => boolean[] | null;
}

interface ITicketExtraData extends ITicketExtraCallbacks {
	varianceList: Map<string, Variance>;
	plan?: Plan;
	userOrder?: number;
}

// BEGIN PLACEHOLDER REPLACE AS PART OF TP-3958 (MOVE)
// this should be moved (and commented and tested) to the rxjs extensions lib
/**
 * Accepts an observable, internally subscribes to it and maintains the last emission. Accepts a callback that will be
 * provided with that last emission and returns a callback that will internally forward args 1 -> n.
 * TLDR, allows for the creation of public callbacks which contain their only private "live" state.
 */
function statergizeCallback<T, R extends unknown[], V>(
	ob$: Observable<T>,
	cb: (val: T, ...theRest: R) => V
): { subscription: Subscription; decoratedCallback: (...theRest: R) => V } {
	let lastEmission: T;
	const subscription = ob$.subscribe((objVal) => {
		lastEmission = objVal;
	});

	const returnFn = (...theRest: R): V => {
		return cb(lastEmission, ...theRest);
	};

	return {
		subscription,
		decoratedCallback: returnFn,
	};
}
// END PLACEHOLDER REPLACE AS PART OF TP-3958 (MOVE)

//changelog vs desktop version
// - if the active line fields aren't defined, return null instead of NaN | nonsense number
function getXFromDate(plan$: Observable<Plan>) {
	return statergizeCallback(plan$, (plan: Plan, date: string, dayOffset?: number) => {
		return plan &&
			plan.view.activeLineDate &&
			plan.view.activeLineX !== undefined &&
			plan.view.activeLineX !== null
			? guessXFromDate(date, dayOffset, plan.view.activeLineDate, plan.view.activeLineX)
			: null;
	});
}

function checkForLongestPull(plan$: Observable<Plan>) {
	return statergizeCallback(plan$, (plan: Plan, pullColumn: number, plannedStart: string) => {
		const pullColumns = plan?.view.pullColumns;

		return !!(
			pullColumns &&
			pullColumns[pullColumn] &&
			pullColumns[pullColumn].plannedStart &&
			pullColumns[pullColumn].plannedStart === plannedStart
		);
	});
}

function getDefaultWorkdaysForLegacyCompletedTickets(calendarWorkdays: CalendarWorkdays) {
	return (startDate: string, finishDate: string, roleId?: string) => {
		if (!calendarWorkdays) {
			return null;
		}
		return calendarWorkdays.calculateWorkdays(startDate, finishDate, roleId);
	};
}

//just to be safe...
function setDatabaseAndViewOnTicket<T extends keyof TicketFields>(
	ticket: Ticket,
	key: T,
	value: TicketFields[T]
) {
	ticket.view[key] = value;
	ticket.database[key] = value;
}

//changelog vs desktop version
// - add exclaimation points to activeLineX and planHasActiveSpace to avoid needing to change types on Ticket and
//maintain essentially broken behavior when compared to desktop...
function decorateInitialProperties(
	ticket: Ticket,
	varianceList: Map<string, Variance>,
	plan?: Plan,
	userOrder?: number
): void {
	ticket.varianceList = varianceList;
	ticket.activeLineX = plan?.view.activeLineX!;
	//internally this is just "activeLineX !== undefined", but I guess it's better to not replicate that logic...
	ticket.planHasActiveSpace = !!plan && !!plan.view.planHasActiveSpace;
	//undecided if this is the best place for this
	setDatabaseAndViewOnTicket(ticket, 'id', ticket.$id);

	if (userOrder !== undefined) {
		setDatabaseAndViewOnTicket(ticket, 'userOrder', userOrder);
	}

	//continue at
	// - figure out how calendarWorkdays should be init'd
	// - create a fully public wrapper function that does all these things in some sense
	// - maybe unit test
	// - integrate into desktop
}

/**
 * Gets back a set of callbacks that can be decorated onto tickets with matching names. Usually these will be passed into [[decorateTicketExtras]]
 * @param plan$ Plan observable, this observable _needs_ to complete on it's own
 * @param calendarWorkdays An
 * @returns
 */
export function getTicketExtraCallbacksForPlan(
	plan$: IObservableLike<Plan>,
	calendarWorkdays: CalendarWorkdays
): ITicketExtraCallbacks {
	const cleanPlan$ = standardizeObservable(plan$);
	return {
		getXFromDate: getXFromDate(cleanPlan$).decoratedCallback,
		checkForLongestPull: checkForLongestPull(cleanPlan$).decoratedCallback,
		getDefaultWorkdaysForLegacyCompletedTickets:
			getDefaultWorkdaysForLegacyCompletedTickets(calendarWorkdays),
	};
}

//TODO fix any's
// - Ticket has a bunch of fields that can be undefined or defined but are largely assume by desktop to always be defined
// - this is mostly fine on desktop because they’re not accessible in practice until they’re defined, but they technically can be
// - desktop arbitrarily swaps between nulls and undefineds. And even has scenarios where nulls are significant data. Rush explicitly forbids nulls
// - rush forbids any’s
// This creates a problem where rush is (correctly) completely preventing code from being written in a typesafe way that does not require potentially extensive changes to desktop code at run time. Leaving basically 2 options:
// - change it, validate and fix all desktop code that this touches. (more correct but aggressive scope creep)
// - any it and disable the check, this can be broken by prettier (moving lines), doesn’t fix the theoretical problem
/**
 * Decorates the current set of ticket extras onto a ticket. Callback functions can be fetched from [[getTicketExtraCallbacksForPlan]]
 */
export function decorateTicketExtras(ticket: Ticket, extras: ITicketExtraData): void {
	//functions
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ticket.getXFromDate = extras.getXFromDate as any;
	ticket.checkForLongestPull = extras.checkForLongestPull;

	ticket.getDefaultWorkdaysForLegacyCompletedTickets =
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		extras.getDefaultWorkdaysForLegacyCompletedTickets as any;

	//data
	decorateInitialProperties(ticket, extras.varianceList, extras.plan, extras.userOrder);
}

export interface ITicketSizeConfig {
	activeColumnWidth: number;
	activeColumnHeight: number;
	pullTicketWidth: number;
	pullTicketHeight: number;
}

const defaultSizeConfig: ITicketSizeConfig = {
	activeColumnWidth: PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH,
	activeColumnHeight: PLAN_CONSTANTS.ACTIVE_COLUMN_HEIGHT,
	pullTicketWidth: PLAN_CONSTANTS.PULL_TICKET_WIDTH,
	pullTicketHeight: PLAN_CONSTANTS.PULL_TICKET_HEIGHT,
};

/**
 * Decorates a ticket with its proper width
 * @param ticketData ticket to decorate
 * @param activeSide true if active side, otherwise false
 * @param workingDays working days array
 * @param config ticket size configuration
 */
export function decorateTicketWidth(
	ticketData: TicketFields,
	activeSide: boolean,
	workingDays: boolean[],
	config: ITicketSizeConfig = defaultSizeConfig
): void {
	if (ticketData.type === TicketType.Constraint || ticketData.Type === TicketType.Milestone) {
		ticketData.width = activeSide ? config.activeColumnWidth : config.pullTicketWidth;
	} else {
		ticketData.width = activeSide ? config.activeColumnWidth * workingDays.length : config.pullTicketWidth;
	}
}

/**
 * Decorates a ticket with its proper height
 * @param ticketData ticket to decorate
 * @param activeSide true if active side, otherwise false
 * @param config ticket size configuration
 */
export function decorateTicketHeight(
	ticketData: TicketFields,
	activeSide: boolean,
	config: ITicketSizeConfig = defaultSizeConfig
): void {
	ticketData.height = activeSide ? config.activeColumnHeight : config.pullTicketHeight;
}

/**
 * Decorates plannedStart, duration days, and working days
 * @param ticketData ticket to decorate
 * @param plannedFinish planned finish date
 */
export function decorateMilestoneOrConstraintTicketSchedule(
	ticketData: TicketFields,
	plannedFinish: string,
	actualFinish?: string
): void {
	ticketData.plannedStart = plannedFinish;
	if (actualFinish) {
		ticketData.actualStart = actualFinish;
	}
	ticketData.durationDays = 1;
	ticketData.workingDays = [true];
}

interface ISplitDateFields {
	actual: TicketFields;
	planned: TicketFields;
	live: TicketFields;
	nonDate: TicketFields;
}

/**
 * Breaks up a set of TicketFields data into the various type of date sets
 */
export function splitTicketFieldsData(ticketFields: TicketFields): ISplitDateFields {
	return Object.keys(ticketFields).reduce(
		(acc, key) => {
			//...if "exists"
			if ((actualOnly[key] ?? null) !== null) {
				acc.actual[key] = ticketFields[key];
			} else if ((plannedOnly[key] ?? null) !== null) {
				acc.planned[key] = ticketFields[key];
			} else if ((reverseLiveMap[key] ?? null) !== null) {
				acc.live[key] = ticketFields[key];
			} else {
				acc.nonDate[key] = ticketFields[key];
			}
			return acc;
		},
		{ actual: {}, planned: {}, live: {}, nonDate: {} } as ISplitDateFields
	);
}
